import { nanoid } from 'nanoid'

import {
  ECampaignAgentJunctionStatus,
  TCampaignAgentPayload,
  TCampaignFact,
  TCampaignAgentJunction,
  TMergeField,
} from '~/features/campaigns/models'
import { getJunctionExecutionStatus } from '~/features/campaigns/utils'
import {
  getAgentMetadataVariable,
  getValueByNormalizedVariable,
  resolveAutoFilledAgentMetadata,
} from '~/features/campaigns/utils/agentMetadata'

const hasOwn = Object.prototype.hasOwnProperty

export const getStringFromKnowledgeFacts = (
  facts: TCampaignFact[] | null | undefined,
) =>
  (facts || [])
    .map((item) => String(item?.text || '').trim())
    .filter(Boolean)
    .join('\n')

function getDraftLocationFieldValue(
  locationFields: Record<string, string>,
  variable: string,
): string | null {
  if (!hasOwn.call(locationFields, variable)) {
    return null
  }

  return String(locationFields[variable] ?? '')
}

export function getLocationFieldInputValue({
  locationFieldsDraft,
  variable,
  junctionMetadata,
}: {
  locationFieldsDraft: Record<string, string>
  variable: string
  junctionMetadata: Record<string, unknown>
}): string {
  const draftValue = getDraftLocationFieldValue(locationFieldsDraft, variable)
  if (draftValue !== null) {
    return draftValue
  }

  return getValueByNormalizedVariable(junctionMetadata, variable)
}

export function getAgentsToStart({
  junctions,
  selectedAgentIds,
  fallbackAgentId = '',
}: {
  junctions: TCampaignAgentJunction[]
  selectedAgentIds: string[]
  fallbackAgentId?: string
}) {
  if (junctions.length === 0) {
    if (!fallbackAgentId) {
      return []
    }
    return [
      {
        agentId: fallbackAgentId,
        status: ECampaignAgentJunctionStatus.started,
      },
    ]
  }

  const selectedSet = new Set(selectedAgentIds)
  return junctions
    .filter(
      (junction) =>
        selectedSet.has(junction.agentId) &&
        getJunctionExecutionStatus(junction) ===
          ECampaignAgentJunctionStatus.notStarted,
    )
    .map((junction) => ({
      agentId: String(junction.agentId),
      status: ECampaignAgentJunctionStatus.started,
    }))
}

type TSharedCampaignAgentSettingLike = {
  locationFields?: Record<string, string | null | undefined>
  customKnowledge?: string
  customRules?: string
}

type TAgentMetadataFieldLike =
  | string
  | TMergeField
  | {
      variable?: string | null
      value?: string | null
      required?: boolean
    }

export function getOrderedAgentMetadataVariables(
  agentMetadataKeys?: TAgentMetadataFieldLike[] | null,
): string[] {
  return Array.from(
    new Set(
      (agentMetadataKeys || [])
        .flatMap((field) => {
          if (typeof field === 'string') {
            return [getAgentMetadataVariable(field)]
          }
          const legacyValue = 'value' in field ? field.value : ''
          return [
            getAgentMetadataVariable(
              String(field.variable || legacyValue || '').trim(),
            ),
          ]
        })
        .filter(Boolean),
    ),
  )
}

/**
 * `metadataVariables` must be every metadata variable the campaign declares
 * (see `getOrderedAgentMetadataVariables`), not just the required ones — an
 * assistant added to a campaign gets every location field the assistant record
 * can supply, so the outreach copy never renders a blank merge field.
 */
export function buildEnableAssistantsPayload<
  TAssistant extends { id?: string | number } & Record<string, unknown>,
>({
  assistantIds,
  assistants,
  metadataVariables,
  sharedCampaignAgentSettings,
}: {
  assistantIds: string[]
  assistants: TAssistant[]
  metadataVariables: string[]
  sharedCampaignAgentSettings?: Record<string, TSharedCampaignAgentSettingLike>
}): TCampaignAgentPayload[] {
  return Array.from(new Set(assistantIds.map(String).filter(Boolean))).map(
    (assistantId) => {
      const assistantRecord = assistants.find(
        (assistant) => String(assistant.id) === assistantId,
      )

      const existingSettings = sharedCampaignAgentSettings?.[assistantId]

      const autoFilledLocationFields = resolveAutoFilledAgentMetadata({
        existingLocationFields: existingSettings?.locationFields,
        assistantRecord,
        metadataVariables,
      })

      const parsedKnowledge = String(existingSettings?.customKnowledge || '')
        .trim()
        .split('\n')
        .map((text) => text.trim())
        .filter(Boolean)
        .map((text) => ({ id: nanoid(), text }))

      const trimmedRules = String(existingSettings?.customRules || '').trim()

      const agentPayload: TCampaignAgentPayload = {
        agentId: assistantId,
        status: ECampaignAgentJunctionStatus.notStarted,
      }

      if (Object.keys(autoFilledLocationFields).length) {
        agentPayload.agentMetadata = autoFilledLocationFields
      }

      if (parsedKnowledge.length) {
        agentPayload.agentCampaignKnowledge = parsedKnowledge
      }

      if (trimmedRules) {
        agentPayload.agentCampaignRules = trimmedRules
      }

      return agentPayload
    },
  )
}
